/**
 * The Follow-ups queue on Home (R23): who to contact today and why, in the
 * order the server ranks them (reason priority, then the longest wait).
 *
 * Shows the first five people and a "See all N follow-ups" control; the
 * bell's follow-ups row links here with `?followUps=all`, which opens the
 * full list. Below the cards, a collapsed "Snoozed (N)" row lists snoozed
 * people with their date and Unsnooze.
 *
 * Message and Call store a pending hand-off; when the realtor comes back the
 * outcome prompt asks what happened, and a recorded conversation is followed
 * by "When should you follow up next?".
 *
 * Once the WhatsApp status has loaded as connected (fetched only when this
 * build has the Meta sign-up config), Message is stored as captured instead
 * (KTD13): coming back checks whether WhatsApp recorded the
 * sent message, with "Checking WhatsApp…" on the person's card, and offers
 * the next step when it did. The outcome prompt opens only when the check
 * finds nothing. Call always asks.
 */

import { ChevronDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
	useClearFollowUpSnooze,
	useFollowUps,
	useInvalidateConversation,
} from "@/features/realtor/hooks/use-realtor-crm";
import { useWhatsappCapture } from "@/features/realtor/hooks/use-whatsapp";
import type {
	FollowUpEntry,
	SnoozedContact,
} from "@/features/realtor/services/realtor-crm-api.service";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { SectionHeading } from "../dashboard/section-heading";
import { FollowUpCard } from "./follow-up-card";
import {
	ACTION_SECONDARY_COMPACT,
	ACTION_SECONDARY_FULL,
	FOLLOW_UPS_ALL,
	FOLLOW_UPS_ANCHOR,
	FOLLOW_UPS_PARAM,
	shortIndiaDate,
} from "./follow-up-format";
import { NextStepPrompt, type NextStepTarget } from "./next-step-prompt";
import { OutcomePrompt } from "./outcome-prompt";
import {
	type HandoffAction,
	type PendingHandoff,
	usePendingHandoff,
} from "./use-pending-handoff";
import { useWhatsappSentCheck } from "./use-whatsapp-sent-check";

/** How many people Home shows before "See all". */
export const QUEUE_PREVIEW_SIZE = 5;

export function FollowUpsQueue() {
	const { t } = useTranslation();
	const query = useFollowUps();
	const [searchParams] = useSearchParams();
	const linkedOpen = searchParams.get(FOLLOW_UPS_PARAM) === FOLLOW_UPS_ALL;
	const [expanded, setExpanded] = useState(linkedOpen);
	const sectionRef = useRef<HTMLElement>(null);
	const headingRef = useRef<HTMLDivElement>(null);
	const cardRefs = useRef(new Map<string, HTMLElement>());
	const { pending, start, finish } = usePendingHandoff();
	const [nextStep, setNextStep] = useState<NextStepTarget | null>(null);
	const whatsapp = useWhatsappCapture();
	const whatsappConnected =
		whatsapp.status.isSuccess &&
		whatsapp.available &&
		whatsapp.connection?.status === "connected";
	const invalidateConversation = useInvalidateConversation();

	useEffect(() => {
		if (!linkedOpen) return;
		setExpanded(true);
		sectionRef.current?.scrollIntoView({ block: "start" });
	}, [linkedOpen]);

	const onHandoff = useCallback(
		(entry: FollowUpEntry, action: HandoffAction) => {
			const leading = entry.reasons[0];
			start({
				contactId: entry.contact.id,
				contactName: entry.contact.name,
				action,
				leadId: leading?.leadId ?? null,
				reasons: entry.reasons.map((reason) => reason.type),
				message: entry.readyMessage,
				listingId:
					leading?.type === "listing_match"
						? (leading.details.listingId ?? null)
						: null,
				captured: action === "message" && whatsappConnected,
			});
		},
		[start, whatsappConnected],
	);

	const onRecorded = useCallback(
		(handoff: PendingHandoff) => {
			finish();
			setNextStep({
				contactId: handoff.contactId,
				contactName: handoff.contactName,
				fromLeadId: handoff.leadId,
			});
		},
		[finish],
	);

	const onCapturedSent = useCallback(
		(handoff: PendingHandoff) => {
			void invalidateConversation(handoff.contactId);
			onRecorded(handoff);
		},
		[invalidateConversation, onRecorded],
	);
	const sentCheck = useWhatsappSentCheck(pending, onCapturedSent);
	const asking = pending && (!pending.captured || sentCheck.fallback);

	const closeNextStep = useCallback(() => setNextStep(null), []);

	// Back to the person's card; the queue heading if the card has gone.
	const returnFocus = (contactId: string) => (event: Event) => {
		event.preventDefault();
		const card = cardRefs.current.get(contactId);
		(card ?? headingRef.current)?.focus();
	};

	const followUps = query.data?.followUps ?? [];
	const visible = expanded ? followUps : followUps.slice(0, QUEUE_PREVIEW_SIZE);

	return (
		<section
			ref={sectionRef}
			id={FOLLOW_UPS_ANCHOR}
			aria-labelledby={`${FOLLOW_UPS_ANCHOR}-title`}
			className="scroll-mt-6 space-y-3 rounded-xl border border-dashboard-border bg-dashboard-bg p-5 shadow-[var(--dashboard-shadow-2)]"
			data-testid="follow-ups-queue"
		>
			<div
				ref={headingRef}
				id={`${FOLLOW_UPS_ANCHOR}-title`}
				tabIndex={-1}
				className="focus:outline-none"
			>
				<SectionHeading
					eyebrow={t("realtor.followUps.queue.eyebrow")}
					title={t("realtor.followUps.queue.title")}
					size={24}
				/>
			</div>

			{query.isPending ? (
				<div className="space-y-3" data-testid="follow-ups-loading">
					{[0, 1, 2].map((i) => (
						<Skeleton
							key={i}
							className="h-14 w-full rounded-xl bg-dashboard-surface"
						/>
					))}
				</div>
			) : query.isError ? (
				<QueryErrorState
					title={t("realtor.followUps.queue.loadFailed")}
					message={query.error.message}
					onRetry={() => void query.refetch()}
					isRetrying={query.isFetching}
				/>
			) : followUps.length === 0 ? (
				<p className="py-6 text-center text-sm text-dashboard-muted">
					{t("realtor.followUps.queue.empty")}
				</p>
			) : (
				<div className="space-y-3">
					<div className="divide-y divide-dashboard-border overflow-hidden rounded-xl border border-dashboard-border">
						{visible.map((entry) => (
							<FollowUpCard
								key={entry.contact.id}
								ref={(node) => {
									if (node) cardRefs.current.set(entry.contact.id, node);
									else cardRefs.current.delete(entry.contact.id);
								}}
								entry={entry}
								onHandoff={onHandoff}
								checkingWhatsapp={
									sentCheck.checking && pending?.contactId === entry.contact.id
								}
							/>
						))}
					</div>
					{followUps.length > QUEUE_PREVIEW_SIZE && (
						<button
							type="button"
							className={ACTION_SECONDARY_FULL}
							aria-expanded={expanded}
							onClick={() => setExpanded((open) => !open)}
						>
							{expanded
								? t("realtor.followUps.queue.showFewer")
								: t("realtor.followUps.queue.seeAll", {
										count: followUps.length,
									})}
						</button>
					)}
				</div>
			)}

			{query.isSuccess && query.data.snoozed.length > 0 && (
				<SnoozedRow snoozed={query.data.snoozed} />
			)}

			{asking && (
				<OutcomePrompt
					key={`${pending.contactId}:${pending.createdAt}`}
					handoff={pending}
					onDismiss={finish}
					onRecorded={onRecorded}
					onCloseAutoFocus={returnFocus(pending.contactId)}
				/>
			)}
			<NextStepPrompt target={nextStep} onClose={closeNextStep} />
		</section>
	);
}

function SnoozedRow({ snoozed }: { snoozed: SnoozedContact[] }) {
	const { t, i18n } = useTranslation();
	const [open, setOpen] = useState(false);
	const clearSnooze = useClearFollowUpSnooze();
	const [failed, setFailed] = useState<{ id: string; message: string } | null>(
		null,
	);
	const listId = `${FOLLOW_UPS_ANCHOR}-snoozed`;

	const unsnooze = async (contactId: string) => {
		setFailed(null);
		try {
			await clearSnooze.mutateAsync(contactId);
		} catch (failure: unknown) {
			setFailed({
				id: contactId,
				message: errorMessage(failure, t("realtor.followUps.snoozed.failed")),
			});
		}
	};

	return (
		<div className="border-t border-dashboard-border pt-3">
			<button
				type="button"
				aria-expanded={open}
				aria-controls={listId}
				onClick={() => setOpen((current) => !current)}
				className="flex min-h-11 w-full items-center justify-between rounded-lg px-1 text-sm font-medium text-dashboard-muted hover:text-dashboard-text"
			>
				{t("realtor.followUps.snoozed.toggle", { count: snoozed.length })}
				<ChevronDown
					aria-hidden
					className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</button>
			{open && (
				<ul id={listId} className="mt-2 space-y-2">
					{snoozed.map(({ contact, until }) => {
						const busy =
							clearSnooze.isPending && clearSnooze.variables === contact.id;
						return (
							<li
								key={contact.id}
								className="rounded-lg border border-dashboard-border bg-dashboard-paper px-3 py-2"
							>
								<div className="flex items-center justify-between gap-3">
									<div className="min-w-0">
										<p className="truncate text-sm font-medium text-dashboard-text">
											{contact.name}
										</p>
										<p className="text-xs text-dashboard-muted">
											{t("realtor.followUps.snoozed.until", {
												date: shortIndiaDate(until, i18n.language),
											})}
										</p>
									</div>
									<button
										type="button"
										className={ACTION_SECONDARY_COMPACT}
										disabled={busy}
										aria-label={t("realtor.followUps.snoozed.unsnoozeAria", {
											name: contact.name,
										})}
										onClick={() => void unsnooze(contact.id)}
									>
										{busy && <Loader2 className="h-4 w-4 animate-spin" />}
										{t("realtor.followUps.snoozed.unsnooze")}
									</button>
								</div>
								{failed?.id === contact.id && (
									<p
										role="alert"
										className="mt-1 text-xs text-dashboard-pill-risk"
									>
										{failed.message}
									</p>
								)}
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
